import React, { useEffect } from 'react';
import { LinkingStats } from '../components/linkingStatus/LinkingStats';
import { LinkingTable } from '../components/linkingStatus/LinkingTable';
import { LinkingComparison } from '../components/linkingStatus/LinkingComparison';
import { Link2, RefreshCw } from 'lucide-react';
import { usePilgrimGuideStore } from '../stores/pilgrimGuideStore';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { ErrorMessage } from '../components/common/ErrorMessage';
import { useLanguage } from '../contexts/LanguageContext';
import { useInView } from 'react-intersection-observer';
import { LoadingProgress } from '../components/common/LoadingProgress';
import { collection, query, where, getDocs, writeBatch, doc, getDoc } from 'firebase/firestore';
import { firestore } from '../lib/firebase/config';
import toast from 'react-hot-toast';

export const LinkingStatusPage = () => {
  const { 
    links, 
    loading, 
    error, 
    initialized,
    hasMore,
    progress,
    fetchLinks,
    loadMore,
    refreshData
  } = usePilgrimGuideStore();
  const { translations } = useLanguage();
  const [isFixing, setIsFixing] = React.useState(false);

  // Configure intersection observer
  const { ref, inView } = useInView({
    threshold: 0.1,
    rootMargin: '100px',
    triggerOnce: false
  });

  // Initialize data
  useEffect(() => {
    if (!initialized) {
      fetchLinks();
    }
  }, [initialized, fetchLinks]);

  // Handle infinite scroll
  useEffect(() => {
    if (inView && hasMore && !loading) {
      loadMore();
    }
  }, [inView, hasMore, loading, loadMore]);

  const handleFixNullOrganizers = async () => {
    try {
      setIsFixing(true);

      // Query for links with null organizerId
      const linksRef = collection(firestore, 'PilgrimGuideLinks');
      const q = query(linksRef, where('organizerId', '==', null));
      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        toast.success('No links with null organizerId found');
        setIsFixing(false);
        return;
      }

      let totalFixed = 0;
      let totalErrors = 0;
      const batchSize = 450; // Firestore batch limit is 500
      let batch = writeBatch(firestore);
      let operationCount = 0;

      for (const docSnapshot of snapshot.docs) {
        try {
          const linkData = docSnapshot.data();
          
          // Get guide data to find correct organizerId
          const guideRef = doc(firestore, 'Guides', linkData.guideId);
          const guideDoc = await getDoc(guideRef);
          
          if (!guideDoc.exists()) {
            console.error(`Guide not found for link ${docSnapshot.id}`);
            totalErrors++;
            continue;
          }

          const guideData = guideDoc.data();
          if (!guideData.organizerId) {
            console.error(`Guide ${linkData.guideId} has no organizerId`);
            totalErrors++;
            continue;
          }

          // Get organizer data to verify it exists
          const organizerRef = doc(firestore, 'Organizers', guideData.organizerId);
          const organizerDoc = await getDoc(organizerRef);
          
          if (!organizerDoc.exists()) {
            console.error(`Organizer ${guideData.organizerId} not found`);
            totalErrors++;
            continue;
          }

          // Update link with correct organizerId and organizerName
          batch.update(docSnapshot.ref, {
            organizerId: guideData.organizerId,
            organizerName: organizerDoc.data().name,
            updatedAt: Date.now()
          });

          operationCount++;
          totalFixed++;

          // Commit batch if it reaches the size limit
          if (operationCount >= batchSize) {
            await batch.commit();
            batch = writeBatch(firestore);
            operationCount = 0;
          }
        } catch (error) {
          console.error(`Error processing link ${docSnapshot.id}:`, error);
          totalErrors++;
        }
      }

      // Commit any remaining operations
      if (operationCount > 0) {
        await batch.commit();
      }

      // Refresh the data
      await refreshData();

      toast.success(`Fixed ${totalFixed} links${totalErrors > 0 ? `, ${totalErrors} errors occurred` : ''}`);
    } catch (error) {
      console.error('Error fixing null organizers:', error);
      toast.error('Failed to fix null organizers');
    } finally {
      setIsFixing(false);
    }
  };

  if (loading && !initialized) return <LoadingSpinner />;
  if (error) return <ErrorMessage message={error} />;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary-50 rounded-lg">
            <Link2 className="text-primary-500" size={24} />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {translations?.linkingStatusPage?.header || 'Linking Status'}
            </h1>
            <p className="text-gray-500 mt-1">
              {translations?.linkingStatusPage?.description || 'Track the linking status between guides and pilgrims'}
            </p>
          </div>
        </div>

        {/* Fix Null Organizers Button */}
                 {/* <button
          onClick={handleFixNullOrganizers}
          disabled={isFixing}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-white transition-colors ${
            isFixing ? 'bg-gray-400 cursor-not-allowed' : 'bg-primary-500 hover:bg-primary-600'
          }`}
        >
          <RefreshCw className={isFixing ? 'animate-spin' : ''} size={20} />
          <span>
            {isFixing ? 'Fixing Null Organizers...' : 'Fix Null Organizers'}
          </span>
        </button> */}
        
      </div>

      {/* Stats */}
      <LinkingStats />

      {/* Comparison Table */}
      <LinkingComparison />

      {/* Loading Progress */}
      {loading && progress.total > 0 && (
        <LoadingProgress
          progress={progress.current}
          total={progress.total}
          label={translations?.linkingStatusPage?.loadingLinks || 'Loading links...'}
        />
      )}

      {/* Table */}
      <LinkingTable />

      {/* Infinite scroll trigger */}
      {hasMore && <div ref={ref} className="h-20 flex items-center justify-center">
        {loading && <LoadingSpinner />}
      </div>}
    </div>
  );
};